/**
 * Copyright (c), Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { GenerationStep } from '../components/GenerationProgress';

interface GenerationContextType {
  isGenerating: boolean;
  currentStep: GenerationStep;
  totalScenes: number;
  error: string | null;
  setGenerationStatus: (status: {
    isGenerating?: boolean;
    currentStep?: GenerationStep;
    totalScenes?: number;
    error?: string | null;
  }) => void;
  startGeneration: () => void;
  endGeneration: (error?: string) => void;
  updateStep: (step: GenerationStep) => void;
  setSceneCount: (count: number) => void;
}

const GenerationContext = createContext<GenerationContextType | undefined>(undefined);

export const GenerationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState<GenerationStep>('preparing');
  const [totalScenes, setTotalScenes] = useState(5); // Default to 5 scenes
  const [error, setError] = useState<string | null>(null);

  const startGeneration = () => {
    setIsGenerating(true);
    setCurrentStep('preparing');
    setError(null);
  };

  const endGeneration = (errorMessage?: string) => {
    if (errorMessage) {
      setError(errorMessage);
      setCurrentStep('error');
    } else {
      setCurrentStep('complete');
    }
    
    // Set isGenerating to false after a delay to allow the completion message to show
    setTimeout(() => {
      setIsGenerating(false);
    }, 3000);
  };

  const updateStep = (step: GenerationStep) => {
    setCurrentStep(step);
  };

  const setSceneCount = (count: number) => {
    setTotalScenes(count);
  };

  const setGenerationStatus = (status: {
    isGenerating?: boolean;
    currentStep?: GenerationStep;
    totalScenes?: number;
    error?: string | null;
  }) => {
    if (status.isGenerating !== undefined) {
      setIsGenerating(status.isGenerating);
    }
    
    if (status.currentStep !== undefined) {
      setCurrentStep(status.currentStep);
    }
    
    if (status.totalScenes !== undefined) {
      setTotalScenes(status.totalScenes);
    }
    
    if (status.error !== undefined) {
      setError(status.error);
    }
  };

  // Testing progress animation - remove in production
  useEffect(() => {
    // Cleanup function
    return () => {
      // Cancel any pending timeouts if component unmounts
    };
  }, []);

  const contextValue: GenerationContextType = {
    isGenerating,
    currentStep,
    totalScenes,
    error,
    setGenerationStatus,
    startGeneration,
    endGeneration,
    updateStep,
    setSceneCount
  };

  return (
    <GenerationContext.Provider value={contextValue}>
      {children}
    </GenerationContext.Provider>
  );
};

// Custom hook to use the generation context
export const useGeneration = (): GenerationContextType => {
  const context = useContext(GenerationContext);
  if (context === undefined) {
    throw new Error('useGeneration must be used within a GenerationProvider');
  }
  return context;
};
